/**
 * A5/A13 — Capability-Matrix (v1.8 AP-10, D-128). Zeilen = Systemlisten-Typen
 * des Workspace, Spalten = das Capability-Set {read · submit · row-edit ·
 * field-edit[Whitelist] · schema-edit}. Für eine Gruppe (`groupId`) gesetzt;
 * gespeichert wird pro (Gruppe × Typ) das ganze Set (Upsert über PUT
 * /capabilities). Nur WS-Admin darf schreiben — sonst antwortet der Server 403,
 * die Matrix bleibt lesbar (D-128: Konfig auf der WS-Rechte-Seite; A5 zeigt sie
 * je Gruppe, Override WS-Admin > Gruppen-Admin).
 *
 * Die Typen leiten sich aus den vorhandenen Systemlisten ab (`capability-types`);
 * bis die Paket-Plattform (AP-13) einen Typ-Katalog liefert, ist die Matrix leer,
 * solange keine Systemliste existiert (ehrlich statt Mock).
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, ryfeApi } from "../api";

import "./ryfe.scss";

import type { Capability } from "../api";

type CapRow = {
  read: boolean;
  submit: boolean;
  rowEdit: boolean;
  schemaEdit: boolean;
  fieldEdit: string;
};

const emptyRow = (): CapRow => ({
  read: false,
  submit: false,
  rowEdit: false,
  schemaEdit: false,
  fieldEdit: "",
});

const rowFromGrant = (g: Capability): CapRow => ({
  read: g.can_read,
  submit: g.can_submit,
  rowEdit: g.can_row_edit,
  schemaEdit: g.can_schema_edit,
  fieldEdit: (g.field_edit || []).join(", "),
});

export const CapabilityMatrix = ({
  wsId,
  groupId,
}: {
  wsId: string;
  groupId: string;
}) => {
  const [types, setTypes] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, CapRow>>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [t, g] = await Promise.all([
        ryfeApi.listCapabilityTypes(wsId),
        ryfeApi.listCapabilities(wsId),
      ]);
      setTypes(t.types);
      const byType: Record<string, CapRow> = {};
      for (const type of t.types) {
        byType[type] = emptyRow();
      }
      for (const grant of g.grants) {
        if (grant.group_id === groupId && byType[grant.list_type]) {
          byType[grant.list_type] = rowFromGrant(grant);
        }
      }
      setRows(byType);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Laden fehlgeschlagen");
    }
  }, [wsId, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  // Persist the whole capability set for one (group × type) on any change.
  const save = async (type: string, next: CapRow) => {
    setRows((prev) => ({ ...prev, [type]: next }));
    try {
      await ryfeApi.setCapability(wsId, {
        groupId,
        listType: type,
        read: next.read,
        submit: next.submit,
        rowEdit: next.rowEdit,
        schemaEdit: next.schemaEdit,
        fieldEdit: next.fieldEdit
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      setError(null);
    } catch (e) {
      // 403 = kein WS-Admin: die Matrix bleibt lesbar, Änderung wird verworfen.
      setError(
        e instanceof ApiError && e.status === 403
          ? "Nur WS-Admin darf Rechte ändern."
          : "Speichern fehlgeschlagen.",
      );
      load();
    }
  };

  if (types.length === 0) {
    return (
      <p className="ryfe-muted" style={{ padding: 0 }}>
        Noch keine Systemlisten-Typen im Workspace — die Rechte-Matrix füllt
        sich, sobald Systemlisten (Pakete) angelegt sind.
      </p>
    );
  }

  const toggle = (type: string, key: keyof CapRow) => {
    const cur = rows[type] || emptyRow();
    save(type, { ...cur, [key]: !cur[key] });
  };

  return (
    <>
      {error && <div className="ryfe-notice ryfe-notice--warn">{error}</div>}
      <div className="ryfe-table-wrap">
        <table className="ryfe-table">
          <thead>
            <tr>
              <th>Systemlisten-Typ</th>
              <th>read</th>
              <th>submit</th>
              <th>row-edit</th>
              <th>field-edit</th>
              <th>schema-edit</th>
            </tr>
          </thead>
          <tbody>
            {types.map((type) => {
              const r = rows[type] || emptyRow();
              const check = (
                key: "read" | "submit" | "rowEdit" | "schemaEdit",
              ) => (
                <td>
                  <input
                    type="checkbox"
                    aria-label={`${key} für ${type}`}
                    checked={r[key]}
                    onChange={() => toggle(type, key)}
                  />
                </td>
              );
              return (
                <tr className="ryfe-row" key={type}>
                  <td>{type}</td>
                  {check("read")}
                  {check("submit")}
                  {check("rowEdit")}
                  <td>
                    <input
                      className="ryfe-field-input"
                      style={{ minWidth: "8rem" }}
                      aria-label={`field-edit für ${type}`}
                      placeholder="Spalten (Komma)"
                      value={r.fieldEdit}
                      onChange={(e) =>
                        setRows((prev) => ({
                          ...prev,
                          [type]: { ...r, fieldEdit: e.target.value },
                        }))
                      }
                      onBlur={() => save(type, rows[type] || r)}
                    />
                  </td>
                  {check("schemaEdit")}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};
